import { wait } from "./time";

const anchorRegex = /^#[^ ]+$/;
const focusableSelector =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * If the provided `href` is an anchor which resolves to an element on the page, scroll to it.
 *
 * Based on https://stackoverflow.com/a/13067009/836995
 *
 * @param href
 *   The href attribute of the <a> tag that was clicked.
 *
 * @return
 *   True if the href was an anchor.
 */
export function scrollIfAnchor(href: string): boolean {
  if (!anchorRegex.test(href)) {
    return false;
  }

  const element = document.getElementById(href.slice(1));
  if (!element) {
    return false;
  }

  element.scrollIntoView({ behavior: "smooth", inline: "nearest" });
  wait(300).then(() => {
    /* We finished scrolling, now we want to move the focus for users relying on keyboard navigation. */

    if (element.matches(focusableSelector)) {
      // The element we scrolled to is focusable. Set keyboard focus there.
      element.focus();
      return;
    }

    const focusableChildren = element.querySelectorAll(focusableSelector);
    if (focusableChildren.length) {
      // The element contains at least one focusable child.
      // Setting the keyboard focus there is better than not moving the focus.
      // Examples include: <sup id="asdf"><a></a></sup> where <sup> is not focusable, but <a> is.

      // @ts-ignore
      focusableChildren[0].focus();

      return;
    }

    // Fall back to trying to focus the element anyway. It might work in obscure cases.
    element.focus();
  });

  // FIXME: Use Vue router API? See https://router.vuejs.org/guide/migration/#usage-of-history-state
  // history.pushState({}, document.title, location.pathname + href
  //
  // => Push state, looks bad idea for that, it "broke" history, too manay useless step on history after some clik on ancor link

  // Browser bug: `pushState` does not affect the CSS target selector.
  // https://bugs.webkit.org/show_bug.cgi?id=83490
  // Therefore we use a class here.
  element.classList.add("withTransition", "targetHighlight");
  wait(2000)
    .then(() => {
      element.classList.remove("targetHighlight");
    })
    .then(() => wait(2000))
    .then(() => {
      element.classList.remove("withTransition");
    });

  return true;
}
